import type {
  MoveRouteSegmentsInput,
  RouteSegment,
} from '../types/surveillanceRoute';
import { isTransportFailure } from '../offline/phase2Transport';

/**
 * «Mover tramos» (plan 2026-10-15 § 3): lo que se decide sin pantalla. El
 * servidor revalida todo; esto solo sirve para elegir el rango, avisar antes
 * de lo que rechazaría y pintar «Así quedará».
 */

const startOf = (segment: Pick<RouteSegment, 'startDate'>) =>
  new Date(segment.startDate).getTime();

/** En orden de hora, como la tabla de Tramos. */
export const orderSegments = <T extends Pick<RouteSegment, 'startDate'>>(
  segments: T[]
): T[] => [...segments].sort((a, b) => startOf(a) - startOf(b));

export type RangeSelection = { anchorId: string | null; ids: string[] };
export const EMPTY_RANGE: RangeSelection = { anchorId: null, ids: [] };

/**
 * D2: los tramos van SEGUIDOS, del Nº N al Nº M. Se eligen en la propia tabla
 * pulsando el primero y el último (en cualquier orden); con un rango hecho,
 * otro toque empieza uno nuevo, y tocar el único marcado lo quita.
 */
export function nextRangeSelection(
  current: RangeSelection,
  clickedId: string,
  segments: RouteSegment[]
): RangeSelection {
  if (
    current.anchorId === clickedId &&
    current.ids.length === 1 &&
    current.ids[0] === clickedId
  ) {
    return EMPTY_RANGE;
  }
  if (!current.anchorId || current.ids.length !== 1) {
    return { anchorId: clickedId, ids: [clickedId] };
  }
  const ordered = orderSegments(segments);
  const a = ordered.findIndex((s) => s.id === current.anchorId);
  const b = ordered.findIndex((s) => s.id === clickedId);
  if (a < 0 || b < 0) return { anchorId: clickedId, ids: [clickedId] };
  const [from, to] = a <= b ? [a, b] : [b, a];
  return {
    anchorId: current.anchorId,
    ids: ordered.slice(from, to + 1).map((s) => s.id),
  };
}

/** El Nº (el de la tabla de Tramos) del primero y del último del rango. */
export function rangeNumbers(
  segments: RouteSegment[],
  ids: string[]
): { first: number; last: number } | null {
  const wanted = new Set(ids);
  const positions = orderSegments(segments)
    .map((s, i) => (wanted.has(s.id) ? i + 1 : 0))
    .filter(Boolean);
  if (positions.length === 0) return null;
  return { first: positions[0], last: positions[positions.length - 1] };
}

/**
 * Lo que el servidor rechazaría del rango, para decirlo antes de pedir los
 * destinos (mismo código que devolvería): un tramo abierto es del cronómetro,
 * una parada registrada (✎) va entera o no va (D12), y una básica ligada a un
 * tramo que va y a otro que se queda no se puede repartir (revisión W3 F5).
 */
export function rangeProblem(
  segments: RouteSegment[],
  ids: string[]
):
  | 'SR_MOVE_SEGMENT_NOT_CLOSED'
  | 'SR_MOVE_SPLITS_STOP_GROUP'
  | 'SR_MOVE_BASIC_SHARED'
  | null {
  const wanted = new Set(ids);
  const moved = segments.filter((s) => wanted.has(s.id));
  if (moved.some((s) => !s.endDate)) return 'SR_MOVE_SEGMENT_NOT_CLOSED';
  const ops = new Set(
    moved.map((s) => s.stopSplit?.operationId).filter(Boolean)
  );
  const splitsGroup = segments.some(
    (s) =>
      !wanted.has(s.id) &&
      !!s.stopSplit?.operationId &&
      ops.has(s.stopSplit.operationId)
  );
  if (splitsGroup) return 'SR_MOVE_SPLITS_STOP_GROUP';
  const basicOf = (s: RouteSegment) => s.linkedBasicActionOrder?.id;
  const movedBasics = new Set(moved.map(basicOf).filter(Boolean));
  const sharesBasic = segments.some((s) => {
    const basic = basicOf(s);
    return !wanted.has(s.id) && !!basic && movedBasics.has(basic);
  });
  return sharesBasic ? 'SR_MOVE_BASIC_SHARED' : null;
}

/**
 * Lo que hay que sacar de la caché tras mover (revisión W3 F4): las Orders
 * que se leyeron antes (gemelas de los tramos y sus básicas ligadas) y las
 * básicas que el servidor dice que cambiaron de recorrido y de ROID (también
 * las sueltas, que la caché del tramo no conoce).
 */
export function orderIdsToEvictAfterMove(
  before: string[],
  result: { movedBasicOrderIds?: string[] | null } | null
): string[] {
  return [...new Set([...before, ...(result?.movedBasicOrderIds ?? [])])];
}

/** El techo del turno de Gestión Horaria (`MAX_SHIFT_MINUTES` del servidor). */
const MAX_SHIFT_MINUTES = 24 * 60;

/**
 * ¿El turno de un recorrido NUEVO con estos tramos pasaría de 24 h? (QA final
 * N1+). El mismo cálculo que el servidor (`computeShiftWindow` +
 * `shiftMinutes`), para avisar y apagar «Mover» antes de pedirlo: cuentan los
 * tramos cerrados de verdad —un cierre forzado, sin PK final ni causa, no sabe
 * cuándo acabó— (o todos los cerrados si no hay otros), del primer inicio al
 * último fin, en minutos enteros.
 */
export function newRouteShiftTooLong(
  segments: Array<
    Pick<RouteSegment, 'startDate' | 'endDate' | 'pkEndKm' | 'pauseCause'>
  >
): boolean {
  const closed = segments.filter((s) => !!s.endDate);
  const real = closed.filter(
    (s) => !(s.pkEndKm == null && s.pauseCause == null)
  );
  const source = real.length > 0 ? real : closed;
  if (source.length === 0) return false;
  const start = Math.min(...source.map((s) => new Date(s.startDate).getTime()));
  const end = Math.max(
    ...source.map((s) => new Date(s.endDate as string).getTime())
  );
  if (!(end > start)) return false;
  const minute = (ms: number) => Math.floor(ms / 60_000);
  return minute(end) - minute(start) > MAX_SHIFT_MINUTES;
}

/** Una operación de «Mover tramos» y la clave con la que viaja. */
export type MoveOperation = { fingerprint: string; clientOpId: string };

/**
 * La clave (`clientOpId`) se conserva solo para reintentar EXACTAMENTE lo
 * mismo; otro rango, otro destino, otro equipo o la casilla de anular estrenan
 * una (revisión lote I1). Con una sola clave por apertura del modal, un
 * segundo intento a otro destino podía tomar como suyo lo que guardó el
 * primero si este acababa de hacer commit.
 */
export function moveOperationFor(
  previous: MoveOperation | null,
  input: Omit<MoveRouteSegmentsInput, 'clientOpId'>,
  newId: () => string = () => crypto.randomUUID()
): MoveOperation {
  const fingerprint = JSON.stringify(input);
  return previous?.fingerprint === fingerprint
    ? previous
    : { fingerprint, clientOpId: newId() };
}

/** Los avisos de «Mover tramos»: uno de cada, aunque lleguen dos a la vez. */
export const MOVE_SEGMENTS_ERROR_TOAST = 'move-segments-error';
export const MOVE_SEGMENTS_DONE_TOAST = 'move-segments-done';
export const MOVE_SEGMENTS_ANNULLED_TOAST = 'move-segments-annulled';

/**
 * ¿El fallo es que no hay conexión (la petición ni llegó a tener respuesta)?
 * Entonces el aviso tiene que decir «Sin conexión», no el error genérico
 * (QA 15-oct). Un 5xx con respuesta o un rechazo del servidor no lo son.
 */
export function isNoConnectionError(error: unknown): boolean {
  const status = (error as { networkError?: { statusCode?: number } } | null)
    ?.networkError?.statusCode;
  return status === undefined && isTransportFailure(error);
}

/**
 * «Así quedará»: los tramos que se quedan en el origen y los del destino con
 * los movidos dentro (a un recorrido nuevo, solo los movidos). La tabla de
 * Tramos los numera sola por su hora, igual que hará el servidor.
 */
export function movePreview(
  origin: RouteSegment[],
  ids: string[],
  targetSegments: RouteSegment[]
): { remaining: RouteSegment[]; target: RouteSegment[]; sourceEmpty: boolean } {
  const wanted = new Set(ids);
  const remaining = orderSegments(origin.filter((s) => !wanted.has(s.id)));
  const moved = origin.filter((s) => wanted.has(s.id));
  return {
    remaining,
    target: orderSegments([...targetSegments, ...moved]),
    sourceEmpty: remaining.length === 0 && moved.length > 0,
  };
}
